import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { refreshAndHardReload } from "@/lib/sw-refresh";

/**
 * Enforces the global "sessions_valid_after" cutoff set by admins when they
 * force everyone to sign in again. If the current access token was issued
 * before the cutoff, the session is dropped immediately instead of waiting
 * for the refresh token revocation to take effect.
 */
function tokenIssuedAt(accessToken: string): number | null {
  try {
    const payload = JSON.parse(atob(accessToken.split(".")[1]));
    return typeof payload?.iat === "number" ? payload.iat * 1000 : null;
  } catch {
    return null;
  }
}

export function useSessionCutoff() {
  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || cancelled) return;

      // Two cutoffs apply: the global one an admin sets for everyone, and a
      // per-user one set when a single account is forced to sign in again.
      const [{ data: policy }, { data: mine }] = await Promise.all([
        supabase.from("session_policy").select("sessions_valid_after").maybeSingle(),
        supabase
          .from("user_session_cutoffs")
          .select("sessions_valid_after")
          .eq("user_id", session.user.id)
          .maybeSingle(),
      ]);
      if (cancelled) return;

      const cutoffs = [policy?.sessions_valid_after, mine?.sessions_valid_after]
        .filter(Boolean)
        .map((v) => new Date(v as string).getTime());
      if (cutoffs.length === 0) return;
      const cutoff = Math.max(...cutoffs);

      const issuedAt = tokenIssuedAt(session.access_token);
      if (issuedAt === null) return;

      if (issuedAt < cutoff) {
        await supabase.auth.signOut({ scope: "local" });
        // Pick up any new build in the same motion as the forced sign-out.
        if (!cancelled) await refreshAndHardReload("/auth");
      }
    };

    check();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") check();
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);
}
